import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    X, Loader2, Mail, Phone, Building2, User, Calendar, Trophy, XCircle,
    Play, Pause, StopCircle, MessageSquare, Send,
    GitBranch, ArrowRightLeft, Eye, MailOpen, MailCheck, Sparkles,
    UserPlus, FileText, Clock, Pencil, Save, Zap,
} from "lucide-react";
import * as api from "../../lib/crmApi";
import TemplatePicker, { SentTemplatesLog } from "./TemplatePicker";
import NoteModal from "./NoteModal";

const fmtDkk = (v) => new Intl.NumberFormat("da-DK").format(v || 0) + " kr.";
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("da-DK", { dateStyle: "short", timeStyle: "short" }) : "—";
const fmtTime = (iso) => iso ? new Date(iso).toLocaleString("da-DK", { hour: "2-digit", minute: "2-digit" }) : "—";

/**
 * Map raw activity kinds (from crm_activities.kind) to a human-readable
 * Danish title + optional detail line + tinted icon. Anything unknown
 * falls back to the raw kind so we never crash on new event types.
 */
function describeActivity(a) {
    const p = a.payload || {};
    const D = { icon: Sparkles, iconClass: "text-slate-400", bg: "bg-slate-50", textClass: "text-slate-800" };
    switch (a.kind) {
        case "lead_created":
            return { ...D, icon: UserPlus, iconClass: "text-emerald-600", bg: "bg-emerald-50",
                title: "Lead oprettet", detail: p.start_automation ? "Automation startet" : null };
        case "flow_started":
            return { ...D, icon: Play, iconClass: "text-sky-600", bg: "bg-sky-50", title: "Flow startet" };
        case "flow_paused":
            return { ...D, icon: Pause, iconClass: "text-amber-600", bg: "bg-amber-50", title: "Flow sat på pause" };
        case "flow_stopped":
            return { ...D, icon: StopCircle, iconClass: "text-rose-600", bg: "bg-rose-50",
                title: "Flow stoppet", detail: p.reason || null };
        case "flow_completed":
            return { ...D, icon: MailCheck, iconClass: "text-emerald-600", bg: "bg-emerald-50", title: "Flow gennemført" };
        case "stage_changed":
            return { ...D, icon: ArrowRightLeft, iconClass: "text-sky-600", bg: "bg-sky-50",
                title: "Status ændret", detail: `${p.from || "—"} → ${p.to || "—"}` };
        case "stage_triggered_email": {
            // The star of the show — highlighted card
            const at = p.fire_at ? ` (sendes kl. ${fmtTime(p.fire_at)})` : "";
            const actor = p.actor ? ` af ${p.actor}` : "";
            const step = p.step_name || (typeof p.step_index === "number" ? `Mail ${p.step_index + 1}` : "Mail");
            return { icon: GitBranch, iconClass: "text-violet-700", bg: "bg-violet-50 border border-violet-200",
                textClass: "text-violet-900",
                title: `${step} udløst`,
                detail: `Status ændret til "${p.trigger_stage}"${actor}${at}` };
        }
        case "stage_triggered_email_sent":
            return { icon: MailCheck, iconClass: "text-violet-700", bg: "bg-violet-50",
                textClass: "text-violet-900",
                title: `${p.step_name || "Mail"} sendt (stage-trigger)`,
                detail: null };
        case "email_sent":
            return { ...D, icon: Mail, iconClass: "text-sky-600", bg: "bg-sky-50",
                title: `${p.step_name || "Mail"} sendt${p.trigger === "manual" ? " (manuelt)" : ""}`,
                detail: p.subject || null };
        case "email_manual_send":
            return { ...D, icon: Zap, iconClass: "text-amber-600", bg: "bg-amber-50",
                title: "Manuel afsendelse udløst",
                detail: p.actor ? `af ${p.actor}` : null };
        case "email_scheduled":
            return { ...D, icon: Clock, iconClass: "text-slate-500", bg: "bg-slate-50",
                title: `${p.step_name || "Næste mail"} skemalagt` };
        case "email_failed":
            return { ...D, icon: XCircle, iconClass: "text-rose-600", bg: "bg-rose-50",
                title: "Mail fejlede", detail: p.error || null };
        case "email_opened":
            return { ...D, icon: Eye, iconClass: "text-emerald-600", bg: "bg-emerald-50",
                title: `Mail åbnet${typeof p.step_index === "number" ? ` (trin ${p.step_index + 1})` : ""}` };
        case "email_replied":
            return { ...D, icon: MailOpen, iconClass: "text-emerald-700", bg: "bg-emerald-50",
                title: "Kunden svarede", detail: p.step_name || null };
        case "note":
            return { ...D, icon: FileText, iconClass: "text-amber-600", bg: "bg-amber-50",
                title: `Note (${p.kind || "andet"})`,
                detail: (p.text || "").slice(0, 120) + (p.text?.length > 120 ? "…" : "") };
        case "meeting_booked":
            return { ...D, icon: Calendar, iconClass: "text-sky-700", bg: "bg-sky-50",
                title: "Møde booket", detail: p.date ? fmtDate(p.date) : null };
        case "won":
            return { ...D, icon: Trophy, iconClass: "text-emerald-700", bg: "bg-emerald-50",
                title: "Vundet", detail: p.value_dkk ? fmtDkk(p.value_dkk) : null };
        case "lost":
            return { ...D, icon: XCircle, iconClass: "text-rose-600", bg: "bg-rose-50",
                title: "Tabt", detail: p.reason || null };
        case "lead_updated":
            return { ...D, icon: User, iconClass: "text-slate-500",
                title: "Lead opdateret",
                detail: p.fields ? p.fields.join(", ") : null };
        default:
            return { ...D, title: a.kind };
    }
}

export default function CrmLeadDrawer({ leadId, onClose, onChanged }) {
    const [lead, setLead] = useState(null);
    const [acts, setActs] = useState([]);
    const [stages, setStages] = useState([]);
    const [reasons, setReasons] = useState([]);
    const [note, setNote] = useState("");
    const [noteKind, setNoteKind] = useState("andet");
    const [followupDate, setFollowupDate] = useState("");
    const [followupTime, setFollowupTime] = useState("09:00");
    const [showWon, setShowWon] = useState(false);
    const [showLost, setShowLost] = useState(false);
    const [showMeeting, setShowMeeting] = useState(false);
    const [editingMaster, setEditingMaster] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [savingMaster, setSavingMaster] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [sendingTemplate, setSendingTemplate] = useState(false);
    const [openNote, setOpenNote] = useState(null);

    const refresh = async () => {
        try {
            const [l, a] = await Promise.all([api.getLead(leadId), api.listActivities(leadId)]);
            setLead(l); setActs(a);
        } catch { toast.error("Kunne ikke hente lead"); }
    };

    const [team, setTeam] = useState([]);

    useEffect(() => {
        refresh();
        api.getStages().then(setStages);
        api.getLostReasons().then(setReasons);
        api.listAdminUsersLight().then(setTeam).catch(() => setTeam([]));
        api.listTemplates().then(setTemplates).catch(() => setTemplates([]));
    }, [leadId]); // eslint-disable-line

    if (!lead) return (
        <div className="fixed inset-0 z-40 bg-slate-900/40" onClick={onClose}>
            <div className="fixed right-0 top-0 bottom-0 w-[560px] bg-white flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
        </div>
    );

    const moveStage = async (stage) => {
        try { await api.moveStage(leadId, stage); toast.success(`Flyttet til ${stage}`); refresh(); onChanged?.(); }
        catch { toast.error("Fejl"); }
    };

    const markReplied = async () => {
        try { await api.markReplied(leadId); toast.success("Markeret som besvaret"); refresh(); onChanged?.(); }
        catch { toast.error("Fejl"); }
    };

    const sendTemplateNow = async (tpl) => {
        if (!window.confirm(`Send "${tpl.name}" til ${lead.email}?`)) return;
        setSendingTemplate(true);
        setPickerOpen(false);
        try {
            await api.sendTemplateToLead(leadId, tpl.id);
            toast.success("Mail sendt");
            refresh(); onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kunne ikke sende");
        } finally {
            setSendingTemplate(false);
        }
    };

    const submitNote = async () => {
        if (!note.trim()) return;
        let followup_at = null;
        if (followupDate) {
            // Convert the naive local datetime to a proper UTC ISO so it
            // compares correctly against the backend's Copenhagen day window
            // (which uses UTC-anchored boundaries). Without this, near-midnight
            // follow-ups can misroute into the wrong day's digest.
            try {
                const local = new Date(`${followupDate}T${followupTime || "09:00"}:00`);
                followup_at = local.toISOString();
            } catch {
                followup_at = `${followupDate}T${followupTime || "09:00"}:00`;
            }
        }
        try {
            await api.addNote(leadId, { text: note, kind: noteKind, followup_at });
            setNote(""); setFollowupDate(""); setFollowupTime("09:00"); setNoteKind("andet");
            refresh();
            toast.success(followup_at ? "Note + opfølgning oprettet" : "Note tilføjet");
        } catch { toast.error("Fejl"); }
    };

    return (
        <div className="fixed inset-0 z-40 bg-slate-900/50" onClick={onClose} data-testid="crm-lead-drawer">
            <div className="fixed right-0 top-0 bottom-0 w-full max-w-[600px] bg-white shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-xs font-semibold uppercase tracking-wider text-sky-700">{lead.stage}</div>
                        <h2 className="font-display text-xl font-bold text-slate-900 truncate">{lead.company}</h2>
                        <div className="text-sm text-slate-500 mt-0.5">{lead.contact_name} · {fmtDkk(lead.value_dkk)}</div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700 flex-shrink-0" data-testid="crm-drawer-close"><X className="h-5 w-5" /></button>
                </div>

                <div className="overflow-y-auto flex-1 p-6 space-y-6">
                    {/* Master data */}
                    <Section title="Stamdata" action={
                        editingMaster ? (
                            <div className="flex items-center gap-1">
                                <button onClick={async () => {
                                    setSavingMaster(true);
                                    try {
                                        // Send only defined fields — empty strings ARE
                                        // valid clears (address, phone, ...) but null value_dkk
                                        // should never be sent.
                                        const payload = { ...editForm };
                                        payload.value_dkk = Number(payload.value_dkk) || 0;
                                        await api.patchLead(leadId, payload);
                                        toast.success("Stamdata opdateret");
                                        setEditingMaster(false);
                                        refresh(); onChanged?.();
                                    } catch (e) {
                                        toast.error(e?.response?.data?.detail || "Fejl ved gem");
                                    } finally { setSavingMaster(false); }
                                }}
                                    disabled={savingMaster}
                                    data-testid="crm-drawer-save-master"
                                    className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 h-8 disabled:opacity-60">
                                    {savingMaster ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Gem
                                </button>
                                <button onClick={() => { setEditingMaster(false); setEditForm({}); }}
                                    className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white text-slate-700 text-xs font-semibold px-3 h-8 hover:bg-slate-50">
                                    Annullér
                                </button>
                            </div>
                        ) : (
                            <button onClick={() => {
                                setEditForm({
                                    company: lead.company || "",
                                    contact_name: lead.contact_name || "",
                                    email: lead.email || "",
                                    phone: lead.phone || "",
                                    address: lead.address || "",
                                    postal_code: lead.postal_code || "",
                                    city: lead.city || "",
                                    value_dkk: lead.value_dkk || 0,
                                    seller: lead.seller || "",
                                    source: lead.source || "",
                                    notes: lead.notes || "",
                                });
                                setEditingMaster(true);
                            }}
                                data-testid="crm-drawer-edit-master"
                                className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white text-slate-600 hover:text-sky-700 hover:bg-slate-50 text-xs font-semibold px-2.5 h-7">
                                <Pencil className="h-3.5 w-3.5" /> Rediger
                            </button>
                        )
                    }>
                        {editingMaster ? (
                            <div className="space-y-3">
                                <EditRow label="Virksomhed" icon={Building2}>
                                    <input value={editForm.company} onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                                        data-testid="edit-company"
                                        className="flex-1 rounded-lg border border-slate-300 h-8 px-2 text-sm" />
                                </EditRow>
                                <EditRow label="Kontakt" icon={User}>
                                    <input value={editForm.contact_name} onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })}
                                        className="flex-1 rounded-lg border border-slate-300 h-8 px-2 text-sm" />
                                </EditRow>
                                <EditRow label="E-mail" icon={Mail}>
                                    <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                        className="flex-1 rounded-lg border border-slate-300 h-8 px-2 text-sm" />
                                </EditRow>
                                <EditRow label="Telefon" icon={Phone}>
                                    <input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                                        className="flex-1 rounded-lg border border-slate-300 h-8 px-2 text-sm" />
                                </EditRow>
                                <EditRow label="Adresse" icon={Building2}>
                                    <input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                                        placeholder="Gadenavn"
                                        className="flex-1 rounded-lg border border-slate-300 h-8 px-2 text-sm" />
                                </EditRow>
                                <EditRow label="Post/By">
                                    <input value={editForm.postal_code} onChange={(e) => setEditForm({ ...editForm, postal_code: e.target.value })}
                                        placeholder="1234" maxLength={20}
                                        className="w-20 rounded-lg border border-slate-300 h-8 px-2 text-sm" />
                                    <input value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                                        placeholder="By"
                                        className="flex-1 rounded-lg border border-slate-300 h-8 px-2 text-sm" />
                                </EditRow>
                                <EditRow label="Værdi (DKK)">
                                    <input type="number" min="0" value={editForm.value_dkk} onChange={(e) => setEditForm({ ...editForm, value_dkk: e.target.value })}
                                        className="w-32 rounded-lg border border-slate-300 h-8 px-2 text-sm" />
                                </EditRow>
                                <EditRow label="Ansv. sælger" icon={User}>
                                    <input value={editForm.seller} onChange={(e) => setEditForm({ ...editForm, seller: e.target.value })}
                                        className="flex-1 rounded-lg border border-slate-300 h-8 px-2 text-sm" />
                                </EditRow>
                                <EditRow label="Kilde">
                                    <input value={editForm.source} onChange={(e) => setEditForm({ ...editForm, source: e.target.value })}
                                        className="flex-1 rounded-lg border border-slate-300 h-8 px-2 text-sm" />
                                </EditRow>
                                <EditRow label="Noter">
                                    <textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                                        rows={3} maxLength={4000}
                                        className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
                                </EditRow>
                            </div>
                        ) : (
                            <>
                                <InfoRow icon={Building2} label="Virksomhed" value={lead.company} />
                                <InfoRow icon={User} label="Kontakt" value={lead.contact_name} />
                                <InfoRow icon={Mail} label="E-mail" value={<a href={`mailto:${lead.email}`} className="text-sky-700 hover:underline">{lead.email}</a>} />
                                <InfoRow icon={Phone} label="Telefon" value={lead.phone || "—"} />
                                {(lead.address || lead.city) && <InfoRow icon={Building2} label="Adresse" value={`${lead.address}, ${lead.postal_code} ${lead.city}`} />}
                                <InfoRow icon={User} label="Ansvarlig sælger" value={lead.seller} />
                                <div className="flex items-center gap-3 py-1.5 text-sm">
                                    <User className="h-4 w-4 text-slate-400 flex-shrink-0" />
                                    <div className="w-32 text-slate-500 text-xs">Lead-ejer</div>
                                    <select
                                        value={lead.owner_email || ""}
                                        data-testid="crm-drawer-owner"
                                        onChange={async (e) => {
                                            try {
                                                await api.patchLead(leadId, { owner_email: e.target.value });
                                                toast.success("Ejer opdateret");
                                                refresh(); onChanged?.();
                                            } catch { toast.error("Fejl"); }
                                        }}
                                        className="flex-1 rounded-lg border border-slate-300 h-8 px-2 text-xs text-slate-800">
                                        <option value="">Ingen (vises for alle)</option>
                                        {team.map((u) => (
                                            <option key={u.email} value={u.email}>{u.name} · {u.email}</option>
                                        ))}
                                    </select>
                                </div>
                                <InfoRow icon={Calendar} label="Oprettet" value={fmtDate(lead.created_at)} />
                                <InfoRow icon={Calendar} label="Senest opdateret" value={fmtDate(lead.updated_at)} />
                                <InfoRow icon={User} label="Kilde" value={lead.source} />
                                {lead.notes && <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm text-slate-700 whitespace-pre-wrap">{lead.notes}</div>}
                            </>
                        )}
                    </Section>

                    {/* Send mail */}
                    <Section title="Send mail">
                        <div className="text-sm text-slate-600 space-y-1 mb-3">
                            <div>Sidst sendt: {fmtDate(lead.last_email_at)} {lead.last_template_name && <span className="text-slate-400">· {lead.last_template_name}</span>}</div>
                            <div>Senest åbnet: {lead.last_email_opened_at ? <span className="text-emerald-700 font-semibold">{fmtDate(lead.last_email_opened_at)}</span> : "—"}</div>
                            {lead.replied_at && <div>Svaret {fmtDate(lead.replied_at)} <span className="text-slate-400">· {lead.replied_to}</span></div>}
                            {lead.unsubscribe_at && <div className="text-rose-700 font-semibold">Kunden har afmeldt</div>}
                        </div>

                        {/* Compact "last 5 sent" strip — så man ikke sender samme mail to gange */}
                        <SentTemplatesLog activities={acts} />

                        {lead.consent && !lead.unsubscribe_at ? (
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => setPickerOpen((v) => !v)}
                                    data-testid="crm-drawer-open-picker"
                                    className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 h-11 text-sm font-semibold bg-sky-600 hover:bg-sky-700 text-white shadow-sm">
                                    <Send className="h-4 w-4" />
                                    {pickerOpen ? "Luk skabelon-vælger" : "Vælg skabelon og send"}
                                </button>
                                {pickerOpen && (
                                    <div className="relative mt-2">
                                        <TemplatePicker
                                            templates={templates}
                                            activities={acts}
                                            onClose={() => setPickerOpen(false)}
                                            onPick={sendTemplateNow}
                                            className="relative w-full"
                                        />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-xs text-slate-500 italic">
                                {lead.unsubscribe_at ? "Leadet er afmeldt — ingen mails kan sendes." : "Leadet mangler consent — ingen mails kan sendes (GDPR)."}
                            </div>
                        )}
                        <div className="mt-3 flex flex-wrap gap-2">
                            <SmallBtn icon={Mail} onClick={markReplied} tone="emerald">Markér som besvaret</SmallBtn>
                        </div>
                    </Section>

                    {/* Stage move */}
                    <Section title="Skift trin">
                        <div className="flex flex-wrap gap-2">
                            {stages.map((s) => (
                                <button key={s} onClick={() => moveStage(s)}
                                    className={`px-3 h-8 rounded-full text-xs font-medium border ${
                                        s === lead.stage ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                                    }`}>{s}</button>
                            ))}
                        </div>
                    </Section>

                    {/* Won/Lost/Meeting */}
                    <Section title="Handlinger">
                        <div className="flex flex-wrap gap-2">
                            <SmallBtn icon={Calendar} onClick={() => setShowMeeting(true)} tone="violet">Book gennemgang</SmallBtn>
                            <SmallBtn icon={Trophy} onClick={() => setShowWon(true)} tone="emerald">Markér vundet</SmallBtn>
                            <SmallBtn icon={XCircle} onClick={() => setShowLost(true)} tone="rose">Markér tabt</SmallBtn>
                        </div>
                        {lead.meeting && (
                            <div className="mt-3 p-3 bg-violet-50 rounded-lg text-sm">
                                <div className="font-semibold text-violet-800">Booket møde</div>
                                <div className="text-violet-700">{lead.meeting.date} kl. {lead.meeting.time} ({lead.meeting.mode})</div>
                                {lead.meeting.link && <a href={lead.meeting.link} target="_blank" rel="noopener noreferrer" className="text-violet-700 underline text-xs">{lead.meeting.link}</a>}
                            </div>
                        )}
                    </Section>

                    {/* Note */}
                    <Section title="Tilføj note + opfølgning">
                        <textarea value={note} onChange={(e) => setNote(e.target.value)}
                            placeholder="Skriv en note..." rows={2}
                            data-testid="crm-note-input"
                            className="w-full rounded-lg border border-slate-300 p-3 text-sm mb-2" />
                        <div className="flex flex-wrap gap-2 mb-2">
                            {[
                                { k: "ringt", label: "Ringet", active: "bg-sky-600 text-white border-sky-600" },
                                { k: "snakket", label: "Snakket", active: "bg-emerald-600 text-white border-emerald-600" },
                                { k: "mail", label: "Mail", active: "bg-violet-600 text-white border-violet-600" },
                                { k: "andet", label: "Andet", active: "bg-slate-700 text-white border-slate-700" },
                            ].map((t) => (
                                <button key={t.k} type="button" onClick={() => setNoteKind(t.k)}
                                    data-testid={`crm-note-kind-${t.k}`}
                                    className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                                        noteKind === t.k ? t.active : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                                    }`}>{t.label}</button>
                            ))}
                        </div>
                        <div className="grid grid-cols-3 gap-2 mb-2">
                            <input type="date" value={followupDate} onChange={(e) => setFollowupDate(e.target.value)}
                                data-testid="crm-note-followup-date"
                                className="col-span-2 rounded-lg border border-slate-300 h-9 px-3 text-sm" placeholder="Opfølgningsdato" />
                            <input type="time" value={followupTime} onChange={(e) => setFollowupTime(e.target.value)}
                                data-testid="crm-note-followup-time"
                                className="rounded-lg border border-slate-300 h-9 px-3 text-sm" />
                        </div>
                        <button onClick={submitNote} className="w-full rounded-lg px-4 h-10 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium inline-flex items-center justify-center gap-2" data-testid="crm-note-submit">
                            <Send className="h-4 w-4" /> Tilføj note
                        </button>
                    </Section>

                    {/* Activity timeline */}
                    <Section title="Aktivitetshistorik">
                        <div className="space-y-2">
                            {acts.map((a) => {
                                const meta = describeActivity(a);
                                const Icon = meta.icon;
                                const isNote = a.kind === "note";
                                return (
                                    <div key={a.id}
                                        role={isNote ? "button" : undefined}
                                        tabIndex={isNote ? 0 : undefined}
                                        onClick={isNote ? () => setOpenNote(a) : undefined}
                                        onKeyDown={isNote ? (e) => { if (e.key === "Enter") setOpenNote(a); } : undefined}
                                        className={`flex gap-3 p-2.5 rounded-lg text-sm ${meta.bg} ${isNote ? "cursor-pointer hover:ring-2 hover:ring-amber-300 transition-shadow" : ""}`}
                                        data-testid={`activity-${a.kind}`}>
                                        <div className="w-28 flex-shrink-0 text-xs text-slate-500">{fmtDate(a.created_at)}</div>
                                        <Icon className={`h-4 w-4 flex-shrink-0 mt-0.5 ${meta.iconClass}`} />
                                        <div className="flex-1 min-w-0">
                                            <div className={`font-semibold ${meta.textClass} flex items-center gap-2`}>
                                                {meta.title}
                                                {isNote && <span className="text-[10px] font-medium text-amber-700 inline-flex items-center gap-1"><Pencil className="h-3 w-3" /> Åbn</span>}
                                                {a.edited_at && <span className="text-[10px] font-normal text-slate-400">(redigeret)</span>}
                                            </div>
                                            {meta.detail && (
                                                <div className="text-xs text-slate-600 mt-0.5">{meta.detail}</div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {acts.length === 0 && <div className="text-sm text-slate-400">Ingen aktivitet endnu</div>}
                        </div>
                    </Section>
                </div>

                {showWon && <WonModal onClose={() => setShowWon(false)} lead={lead} onDone={() => { setShowWon(false); refresh(); onChanged?.(); }} />}
                {showLost && <LostModal onClose={() => setShowLost(false)} reasons={reasons} onDone={() => { setShowLost(false); refresh(); onChanged?.(); }} leadId={leadId} />}
                {showMeeting && <MeetingModal onClose={() => setShowMeeting(false)} onDone={() => { setShowMeeting(false); refresh(); onChanged?.(); }} leadId={leadId} sellerDefault={lead.seller} />}
                {openNote && <NoteModal activity={openNote} leadId={leadId} onClose={() => setOpenNote(null)} onDone={() => { setOpenNote(null); refresh(); onChanged?.(); }} />}
            </div>
        </div>
    );
}

function Section({ title, children, action }) {
    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</h3>
                {action}
            </div>
            {children}
        </div>
    );
}

function InfoRow({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-3 py-1.5 text-sm">
            <Icon className="h-4 w-4 text-slate-400 flex-shrink-0" />
            <div className="w-32 text-slate-500 text-xs">{label}</div>
            <div className="text-slate-800 flex-1 min-w-0 truncate">{value}</div>
        </div>
    );
}

function EditRow({ icon: Icon, label, children }) {
    return (
        <div className="flex items-start gap-3 text-sm">
            {Icon ? <Icon className="h-4 w-4 text-slate-400 flex-shrink-0 mt-2" /> : <div className="h-4 w-4 flex-shrink-0" />}
            <div className="w-28 text-slate-500 text-xs pt-2">{label}</div>
            <div className="flex-1 flex gap-2">
                {children}
            </div>
        </div>
    );
}

function SmallBtn({ icon: Icon, children, tone, ...rest }) {
    const toneClasses = {
        emerald: "bg-emerald-600 hover:bg-emerald-700 text-white",
        rose: "bg-rose-600 hover:bg-rose-700 text-white",
        violet: "bg-violet-600 hover:bg-violet-700 text-white",
    }[tone] || "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50";
    return (
        <button {...rest} className={`inline-flex items-center gap-1.5 rounded-lg px-3 h-9 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed ${toneClasses}`}>
            {Icon && <Icon className={`h-3.5 w-3.5 ${Icon === Loader2 ? "animate-spin" : ""}`} />} {children}
        </button>
    );
}

function WonModal({ onClose, lead, onDone }) {
    const [v, setV] = useState(lead.value_dkk || 0);
    const [saving, setSaving] = useState(false);
    return (
        <ModalShell title="Markér som vundet" onClose={onClose}>
            <label className="block text-sm font-medium text-slate-700 mb-1">Endelig salgsværdi (DKK)</label>
            <input type="number" value={v} onChange={(e) => setV(e.target.value)} data-testid="crm-won-value"
                className="w-full rounded-lg border border-slate-300 h-10 px-3 text-sm" />
            <div className="mt-4 flex justify-end gap-2">
                <button onClick={onClose} className="px-4 h-10 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Annuller</button>
                <button disabled={saving} onClick={async () => { setSaving(true); try { await api.markWon(lead.id, Number(v) || 0); toast.success("Markeret som vundet"); onDone(); } catch { toast.error("Fejl"); } finally { setSaving(false); } }}
                    className="px-4 h-10 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold" data-testid="crm-won-confirm">
                    Bekræft
                </button>
            </div>
        </ModalShell>
    );
}

function LostModal({ onClose, reasons, onDone, leadId }) {
    const [reason, setReason] = useState(reasons[0] || "");
    const [comment, setComment] = useState("");
    const [followup, setFollowup] = useState("");
    const [saving, setSaving] = useState(false);
    return (
        <ModalShell title="Markér som tabt" onClose={onClose}>
            <label className="block text-sm font-medium text-slate-700 mb-1">Tabsårsag *</label>
            <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full rounded-lg border border-slate-300 h-10 px-3 text-sm" data-testid="crm-lost-reason">
                {reasons.map((r) => <option key={r}>{r}</option>)}
            </select>
            <label className="block text-sm font-medium text-slate-700 mb-1 mt-3">Kommentar</label>
            <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} className="w-full rounded-lg border border-slate-300 p-3 text-sm" />
            <label className="block text-sm font-medium text-slate-700 mb-1 mt-3">Senere opfølgning (valgfri)</label>
            <input type="date" value={followup} onChange={(e) => setFollowup(e.target.value)} className="w-full rounded-lg border border-slate-300 h-10 px-3 text-sm" />
            <div className="mt-4 flex justify-end gap-2">
                <button onClick={onClose} className="px-4 h-10 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Annuller</button>
                <button disabled={saving} onClick={async () => { setSaving(true); try { await api.markLost(leadId, { reason, comment, followup_date: followup || null }); toast.success("Markeret som tabt"); onDone(); } catch { toast.error("Fejl"); } finally { setSaving(false); } }}
                    className="px-4 h-10 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold" data-testid="crm-lost-confirm">
                    Bekræft
                </button>
            </div>
        </ModalShell>
    );
}

function MeetingModal({ onClose, onDone, leadId, sellerDefault }) {
    const [f, setF] = useState({ date: "", time: "14:00", mode: "online", link: "https://cal.com/cleany-dk/20min", address: "", seller: sellerDefault, notes: "" });
    const [saving, setSaving] = useState(false);
    return (
        <ModalShell title="Book gennemgang" onClose={onClose}>
            <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-slate-700 mb-1">Dato</label>
                    <input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="crm-meeting-date"
                        className="w-full rounded-lg border border-slate-300 h-10 px-3 text-sm" /></div>
                <div><label className="block text-xs font-medium text-slate-700 mb-1">Klokkeslæt</label>
                    <input type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 h-10 px-3 text-sm" /></div>
                <div><label className="block text-xs font-medium text-slate-700 mb-1">Mødeform</label>
                    <select value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })} className="w-full rounded-lg border border-slate-300 h-10 px-3 text-sm">
                        <option value="online">Online</option><option value="fysisk">Fysisk</option>
                    </select></div>
                <div><label className="block text-xs font-medium text-slate-700 mb-1">Ansvarlig</label>
                    <input value={f.seller} onChange={(e) => setF({ ...f, seller: e.target.value })} className="w-full rounded-lg border border-slate-300 h-10 px-3 text-sm" /></div>
                <div className="col-span-2"><label className="block text-xs font-medium text-slate-700 mb-1">Mødelink / adresse</label>
                    <input value={f.mode === "online" ? f.link : f.address}
                        onChange={(e) => setF(f.mode === "online" ? { ...f, link: e.target.value } : { ...f, address: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 h-10 px-3 text-sm" /></div>
                <div className="col-span-2"><label className="block text-xs font-medium text-slate-700 mb-1">Noter</label>
                    <textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 p-3 text-sm" /></div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
                <button onClick={onClose} className="px-4 h-10 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Annuller</button>
                <button disabled={saving || !f.date} onClick={async () => { setSaving(true); try { await api.bookMeeting(leadId, f); toast.success("Møde booket"); onDone(); } catch { toast.error("Fejl"); } finally { setSaving(false); } }}
                    className="px-4 h-10 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold disabled:opacity-50" data-testid="crm-meeting-confirm">
                    Book møde
                </button>
            </div>
        </ModalShell>
    );
}

function ModalShell({ title, children, onClose }) {
    return (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                <h3 className="font-display text-lg font-bold text-slate-900 mb-4">{title}</h3>
                {children}
            </div>
        </div>
    );
}
